import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { ActivityIndicator } from '@common/activity-indicator/activity-indicator';
import seoService from '@services/seo-service/seo-service';
import ServerContext from '@contexts/serverContext';
import { useAppInformationContent } from '@sections/breadcrumbs/hook/use-breadcrumb-content';
import ScrollUtil from '@utils/scroll-to-top-util/scroll-to-top-util';
import SignInAndSecurity from '@sections/sign-in-and-security/sign-in-and-security';
import './sign-in-credentials-view.scss';
import {
    SecurityContent,
    useSecurityContent,
} from '@sections/sign-in-and-security/hook/use-security-content';
import MFAService, {
    MFAOptions,
    MFAResponse,
} from '@services/mfa-service/mfa-service';
import HttpService from '@services/http-service/http-service';
import { findPathByAlias } from '@routes/routesList';
import AuthenticationService from '@services/authentication-service/authentication-service';
import { isWebviewFromSearch } from '@services/authentication-service/utils/is-webview';
import {
    CUSTOM_OTP_COUNTRY_PARAMS,
    FORDPASS_AUTHORIZED_DEEPLINK,
    LINCOLNWAY_AUTHORIZED_DEEPLINK,
    RTL_LANGUAGES,
    SUPPRESS_US_COOKIE_BANNER_COUNTRIES,
} from '@constants';
import AppConfigurationService from '@services/app-configuration-service/app-configuration-service';
import {
    NotificationType,
    useNotificationContext,
} from '@contexts/notificationContext';
import { useAnalytics } from '@/hooks/use-analytics';
import { Breadcrumbs } from '@/components/sections/breadcrumbs/breadcrumbs';
import { PasskeyNotifications } from '@views/sign-in-credentials-view/passkeys/passkeys-section';

export interface CustomMarketParams {
    countryCode: string;
    customMarket: string;
    uiLocales: string;
}

const SignInCredentialsView = () => {
    const { setNotificationContext } = useNotificationContext();
    const serverContext = useContext(ServerContext);
    const appConfig = new AppConfigurationService();
    const httpService = HttpService;
    const authenticationService = new AuthenticationService();
    const mfaService: MFAService = new MFAService(httpService, appConfig);
    const securityContent: SecurityContent = useSecurityContent();
    const appInformationContent = useAppInformationContent();
    const scrollUtil = new ScrollUtil();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const fromPasskeyAdd = queryParams.get('passkeyAdd') || undefined;
    const fromPasskeyRemove = queryParams.get('passkeyRemove') || undefined;
    // The webview service hands the app `webview=true`; `featureName` is the
    // legacy signal and survives inside the OAuth state after a B2C return.
    const isWebview = isWebviewFromSearch(location.search);
    const locale = (queryParams.get('locale') || '').toLowerCase();
    const isRtlLocale = RTL_LANGUAGES.includes(locale);
    const isRtlWebview = isWebview && isRtlLocale;
    const fromAppLogout = queryParams.get('appLogout');
    const [showMFASection, setShowMFASection] = useState<boolean>(false);
    const [mfaOptions, setMfaOptions] = useState<MFAOptions[]>(null);
    const [userRequiresMFA, setUserRequiresMFA] = useState<boolean>(false);
    const [errorNotificationMessage, setErrorNotificationMessage] =
        useState<string>(null);
    const [hasPasskey, setHasPasskey] = useState<boolean | null>(null);
    const [passkeyRegistrationTimestamp, setPasskeyRegistrationTimestamp] =
        useState<string>(null);
    const [passkeysNotification, setPasskeysNotification] =
        useState<PasskeyNotifications>(null);
    const [showErrorPage, setShowErrorPage] = useState<boolean>(false);
    const isFord = appConfig.getBrand() === 'ford';
    const isLincoln = appConfig.getBrand() === 'lincoln';
    const [fireAnalytics] = useAnalytics();
    const [mfaCallComplete, setMfaCallComplete] = useState<boolean>(false);
    const [mfaCallInitiated, setMfaCallInitiated] = useState<boolean>(false);
    const showSignInAndSecurity = Boolean(securityContent);

    authenticationService.updateState(
        findPathByAlias('SignInCredentialsView') + location.search
    );

    seoService.set(
        {
            pageTitle: 'Sign In and Security',
            applicationName: appInformationContent?.applicationName,
            pageDescription: 'Sign In and Security Description',
        },
        serverContext
    );

    const logoutUsingDeepLink = () => {
        if (isFord) window.location.assign(FORDPASS_AUTHORIZED_DEEPLINK);
        else if (isLincoln)
            window.location.assign(LINCOLNWAY_AUTHORIZED_DEEPLINK);
    };

    const handlePasskeyStatus = (mfaServiceResponse: MFAResponse): void => {
        if (mfaServiceResponse.publicKeyCredentialPublicKey) {
            setHasPasskey(true);

            if (mfaServiceResponse.passkeyRegistrationTimestamp) {
                setPasskeyRegistrationTimestamp(
                    mfaServiceResponse.passkeyRegistrationTimestamp
                );
            } else {
                setPasskeyRegistrationTimestamp('');
            }
        } else {
            setHasPasskey(false);
            setPasskeyRegistrationTimestamp('');
        }
    };

    const handleMFAServiceResponse = (
        mfaServiceResponse: MFAResponse
    ): void => {
        setShowMFASection(true);
        setMfaCallComplete(true);
        setUserRequiresMFA(mfaServiceResponse.userRequiresMFA);
        setMfaOptions(mfaServiceResponse.mfaOptions);
        handlePasskeyStatus(mfaServiceResponse);
    };

    const makeMFACall = (isRetry = false): void => {
        mfaService
            .getMFAOptions()
            .then((mfaServiceResponse) => {
                if (mfaServiceResponse) {
                    const isInvalidPasskeyState =
                        Boolean(
                            fromPasskeyAdd &&
                                !mfaServiceResponse.publicKeyCredentialPublicKey
                        ) ||
                        Boolean(
                            fromPasskeyRemove &&
                                mfaServiceResponse.publicKeyCredentialPublicKey
                        );

                    if (!isRetry && isInvalidPasskeyState) {
                        console.warn('MFA passkey data not ready, retrying...');
                        makeMFACall(true);
                    } else {
                        handleMFAServiceResponse(mfaServiceResponse);
                    }
                } else {
                    handleGetMFAError();
                }
            })
            .catch(() => {
                handleGetMFAError();
            });
    };

    const getMFAOptions = () => {
        clearErrorNotification();
        authenticationService
            .onIsAuthenticated()
            .then((isAuthenticated: boolean) => {
                if (isAuthenticated) {
                    makeMFACall();
                } else {
                    authenticationService.login();
                }
            });
    };

    const handleGetMFAError = () => {
        setShowMFASection(false);
        setMfaCallComplete(true);
        setErrorNotification(
            securityContent.mfaGetOptionsErrorNotificationMessage
        );
    };

    const setErrorNotification = (message: string) => {
        if (isLincoln) {
            if (message)
                setNotificationContext(NotificationType.Error, false, message);
        } else {
            setErrorNotificationMessage(message);
        }
    };

    const clearErrorNotification = () => {
        if (isLincoln) {
            setNotificationContext(NotificationType.None, false);
        } else {
            setErrorNotificationMessage(null);
        }
    };

    useEffect(() => {
        if (securityContent && !mfaCallInitiated) {
            setMfaCallInitiated(true);
            getMFAOptions();
        }
    }, [securityContent, mfaCallInitiated]);

    useEffect(() => {
        if (fromAppLogout) {
            logoutUsingDeepLink();
        }

        if (
            !SUPPRESS_US_COOKIE_BANNER_COUNTRIES.includes(
                queryParams.get('locale')
            )
        ) {
            fireAnalytics('ownerAccountSignIn');
        }
    }, []);

    useEffect(() => {
        scrollUtil.scrollPageToTop();
    }, []);

    return (
        <div
            dir={isRtlWebview ? 'rtl' : undefined}
            className={[
                'sign-in-credentials-view',
                isWebview ? 'webview' : '',
                isRtlWebview ? 'rtl-webview' : '',
            ]
                .filter(Boolean)
                .join(' ')}
        >
            {!isWebview && (
                <Breadcrumbs
                    currentPage={
                        securityContent?.signInSecurityButtonText || ''
                    }
                    fromSignIn
                />
            )}
            {showSignInAndSecurity && (
                <SignInAndSecurity
                    country={appConfig.currentCountryCode}
                    mfaService={mfaService}
                    showMFASection={showMFASection}
                    mfaOptions={mfaOptions}
                    setMFAOptions={setMfaOptions}
                    userRequiresMFA={userRequiresMFA}
                    setUserRequiresMFA={setUserRequiresMFA}
                    hasPasskey={hasPasskey}
                    passkeyRegistrationTimestamp={passkeyRegistrationTimestamp}
                    securityContent={securityContent}
                    fromWebview={isWebview}
                    errorNotificationMessage={errorNotificationMessage}
                    setErrorNotification={setErrorNotification}
                    clearErrorNotification={clearErrorNotification}
                    passkeysNotification={passkeysNotification}
                    setPasskeysNotification={setPasskeysNotification}
                    showErrorPage={showErrorPage}
                    setShowErrorPage={setShowErrorPage}
                    scrollUtil={scrollUtil}
                    logoutUsingDeepLink={logoutUsingDeepLink}
                    isLincoln={isLincoln}
                    isRtlWebview={isRtlWebview}
                    customOTP={CUSTOM_OTP_COUNTRY_PARAMS.find(
                        (market) =>
                            market.customMarket === queryParams.get('locale')
                    )}
                />
            )}
            {mfaCallInitiated && !mfaCallComplete ? (
                <ActivityIndicator className="fds-activity-indicator__center" />
            ) : null}
        </div>
    );
};

export default SignInCredentialsView;
